import HeaderSection from '@/components/Header/HeaderSection.vue';
import tile_card from '@/components/tile_card/TIleCardView.vue';
import DetailsCard from '@/components/Details_card/DetailCardView.vue';
import OverviewSummary from '@/components/OverviewSummary/OverviewSummary.vue';
import PortfolioSuperSetChart from '@/components/PortfolioSuperSetChart/PortfolioSuperSetChart.vue';
import WelcomeBack from '@/components/WelcomeBack/WelcomeBack.vue';
import PropertyOverview from '@/components/PropertyOverview/PropertyOverview.vue';
import QuickActions from '@/components/QuickActions/QuickActions.vue';
import ServiceProvider from '@/components/ServiceProvider/ServiceProvider.vue';
import PortfolioSidebar from '@/components/PortfolioSidebar/PortfolioSidebar.vue';
import PropertySidebar from '@/components/PropertySidebar/PropertySidebar.vue';
import BolideAssistant from '@/components/BolideAssistant/BolideAssistant.vue';
import { globalState } from '@/config/globalState';
import { get_token, get_user_profile } from '@/router';
import getTilesConfiguration from '@/data/tiles';
import {
  infinity_dashboard_id,
  global_executive_dashboard_id,
  property_specific_dashboards as imported_property_specific_dashboards,
  property_dashboards as imported_property_dashboards
} from '@/config/dashboardIds';
import ShiftSchedule from '@/components/ShiftSchedule/ShiftSchedule.vue';
import RecentDocuments from '@/components/RecentDocuments/RecentDocuments.vue';
import BolideLoader from '@/components/BolideLoader/BolideLoader.vue';
import SecurityDemographicsCompetition from '@/components/SecurityDemographicsCompetition/SecurityDemographicsCompetition.vue';

// Environment variables
const superset_base_domain = process.env.VUE_APP_SUPERSET_BASE_URL;
const guest_token_url = process.env.VUE_APP_GUEST_TOKEN_URL;
const superset_sdk_url = process.env.VUE_APP_SUPERSET_SDK_URL;

// Superset Config
const superset_config = {
  baseUrl: superset_base_domain,
  defaultDashboardIds: {
    main: infinity_dashboard_id
  }
};

const auth_config = {
  guestTokenUrl: guest_token_url
};

// Token Cache
const token_cache = {
  tokens: new Map(),

  get(dashboardId) {
    const cached = this.tokens.get(dashboardId);
    if (cached && Date.now() < cached.expiresAt) return cached.token;
    if (cached) this.tokens.delete(dashboardId);
    return null;
  },

  set(dashboardId, token, expiresInMs = 55 * 60 * 1000) {
    this.tokens.set(dashboardId, {
      token,
      expiresAt: Date.now() + expiresInMs
    });
  },

  clear() { this.tokens.clear(); },

  cleanup() {
    const now = Date.now();
    for (const [dashboardId, data] of this.tokens.entries()) {
      if (now >= data.expiresAt) this.tokens.delete(dashboardId);
    }
  }
};

function safe_parse_maybe_double_encoded_json(text) {
  let parsed = JSON.parse(text);
  if (typeof parsed === 'string') parsed = JSON.parse(parsed);
  return parsed;
}

function is_valid_dashboard_id(id) {
  return typeof id === 'string' && id.trim() !== '';
}

export default {
  name: 'DashboardView',

  components: {
    tile_card,
    HeaderSection,
    DetailsCard,
    OverviewSummary,
    PortfolioSuperSetChart,
    WelcomeBack,
    PropertyOverview,
    QuickActions,
    ServiceProvider,
    PortfolioSidebar,
    PropertySidebar,
    BolideAssistant,
    RecentDocuments,
    ShiftSchedule,
    BolideLoader,
    SecurityDemographicsCompetition
  },

  data() {
    return ({
      // View State
      current_view: 'main',
      current_dashboard_type: 'global',
      active_menu_item: 'portfolio-comparisons',
      current_embedding_dashboard_id: null,

      // Data
      tiles: [],
      breadcrumb: [],
      selected_property: null,

      // UI State
      iframe_loading: false,
      iframe_error: false,
      show_all: false,
      right_panel_collapsed: false,
      is_mobile: false,
      mobile_sidebar_open: false,
      window_width: 0,
      _messageListener: null,

      // Superset State
      sdk_loaded: false,
      sdk_configured: false,
      initialization_complete: false,
      auth_token: null,
      embedded_dashboards: new Set(),
      token_refresh_in_progress: false,
      show_assistant: false,
      sidebar_collapsed: false,
      min_loader_time: 3500,
      loader_start_time: null,
      is_loading: true,
      user: null,

      // Dashboard Mappings
      global_dashboards: ({
        'executive-summary': global_executive_dashboard_id,
      }),

      // Property dashboard mapping - Universal dashboards (fallback)
      property_dashboards: imported_property_dashboards,

      // Property-specific dashboard mappings (now loaded from config)
      property_specific_dashboards: imported_property_specific_dashboards,
    });
  },

  computed: {
    currentCrimeStats() {
      if (!this.selected_property || !this.selected_property.crimeStats) {
        return {};
      }
      return this.selected_property.crimeStats;
    },

    visible_tiles() {
      return this.show_all ? this.tiles : this.tiles.slice(0, 3);
    },

    showRightPanel() {
      return this.is_mobile || !this.right_panel_collapsed;
    },

    current_dashboard_id() {
      if (this.current_dashboard_type === 'global') {
        return this.global_dashboards[this.active_menu_item] || global_executive_dashboard_id;
      }

      if (this.selected_property) {
        const propertyName = this.selected_property.title;
        const propertyDashboards = this.property_specific_dashboards[String(propertyName)];

        const specificDashboardId = propertyDashboards?.[this.active_menu_item];
        if (is_valid_dashboard_id(specificDashboardId)) {
          return specificDashboardId;
        }

        const propertyDashboardId = this.property_dashboards[this.active_menu_item];
        if (is_valid_dashboard_id(propertyDashboardId)) {
          return propertyDashboardId;
        }

        if (is_valid_dashboard_id(this.selected_property.dashboardId)) {
          return this.selected_property.dashboardId;
        }
      }
      return null;
    },

    is_property_view() {
      return this.current_dashboard_type === 'property' && this.selected_property;
    },

    /**
     * -----------------------------------------------
     * NEW COMPUTED: First Management Contact
     * -----------------------------------------------
     */
    management_contact() {
      const card = this.selected_property?.tileCards?.find(
        tileCard =>
          tileCard.type === "details" &&
          (
            tileCard.title?.toLowerCase().includes("management") ||
            tileCard.title?.toLowerCase().includes("on-site")
          )
      );

      if (!card) return null;

      // ✅ NEW STRUCTURE (contacts array)
      if (Array.isArray(card.contacts) && card.contacts.length) {
        const preferred =
          card.contacts.find(c =>
            (c.role || "").toLowerCase().includes("centre manager")
          ) ||
          card.contacts.find(c =>
            (c.status || "").toLowerCase() === "on-site"
          ) ||
          card.contacts[0];

        return ({
          full_name: preferred?.full_name || "N/A",
          phone: preferred?.phone || "N/A",
          email: preferred?.email || "N/A",
          role: preferred?.role || "Centre Manager"
        });
      }

      // ✅ OLD STRUCTURE fallback
      const managementDetails = card.details;
      if (!managementDetails) return null;

      return ({
        full_name: managementDetails.hours?.split(":")[0]?.trim() || "N/A",
        phone: managementDetails.phone || "N/A",
        email: managementDetails.email || "N/A",
        role: "Centre Manager"
      });
    }

  },


  mounted() {
    this.is_loading = true;
    this.loader_start_time = Date.now();  // Record loader start moment
    this.user = get_user_profile();

    this.$nextTick(() => {
      this.initializeDashboard();
    });

    // Fallback if superset never loads
    setTimeout(() => {
      if (this.is_loading) {
        this.is_loading = false;
      }
    }, 10000); // 10s fallback

    // 4️⃣ Load tiles immediately (from external file)
    this.tiles = getTilesConfiguration().map(tile => ({
      ...tile,
      is_live: tile.id.includes("(Live)")
    }));

    globalState.setTiles(this.tiles);
    globalState.setUser(this.user);

    // 5️⃣ Setup responsiveness
    this.handleResize();
    window.addEventListener('resize', this.handleResize);

    // 6️⃣ Set initial breadcrumb
    this.breadcrumb = [{ id: 'main', title: 'Centre Dashboard' }];

    // 7️⃣ Token cleanup
    this.tokenCleanupInterval = setInterval(() => {
      token_cache.cleanup();
    }, 60000);
  },

  beforeUnmount() {
    this.cleanupDashboards();
    if (this.tokenCleanupInterval) clearInterval(this.tokenCleanupInterval);
    window.removeEventListener('resize', this.handleResize);
    this._cleanupMessageListener();
  },

  methods: {
    _setupMessageListener() {
      if (this._messageListener) return; // Already set up
      
      this._messageListener = (event) => {
        if (event.data?.type === "dashboardLoadComplete" ||
            event.data?.event === "dashboard_loaded") {
          if (this.is_loading) {
            this.is_loading = false;
          }
        }
      };
      
      window.addEventListener("message", this._messageListener);
    },

    _cleanupMessageListener() {
      if (this._messageListener) {
        window.removeEventListener("message", this._messageListener);
        this._messageListener = null;
      }
    },

    toggleSidebar() {
      this.sidebar_collapsed = !this.sidebar_collapsed;
    },
    toggleAssistant(value) {
      globalState.toggleAssistant(value);
    },
    // ========== DASHBOARD INITIALIZATION ==========
    async initializeDashboard() {
      try {
        this.iframe_loading = true;
        this.iframe_error = false;
        this.auth_token = get_token();

        if (!this.auth_token) throw new Error('No authentication token found.');

        await this.initializeSupersetSDK();
        await this.configureSupersetSDK();

        // Wait for DOM to be updated before embedding
        await this.$nextTick();
        await this.embedDashboard('dashboard-main', this.current_dashboard_id);

        // Superset is ready → hide loader
        const elapsed = Date.now() - this.loader_start_time;

        if (elapsed >= this.min_loader_time) {
          // Show loader for at least min time
          this.is_loading = false;
        } else {
          // Add delay to complete minimum duration
          const remaining = this.min_loader_time - elapsed;
          setTimeout(() => {
            this.is_loading = false;
          }, remaining);
        }

        this.initialization_complete = true;

      } catch (error) {
        console.error('Dashboard initialization failed:', error);
        this.iframe_error = true;
        this.iframe_loading = false;
      }
    },

    ensureAuthToken() {
      if (!this.auth_token) {
        this.auth_token = get_token();
      }
      if (!this.auth_token) {
        throw new Error('No authentication token found.');
      }
    },

    async waitForTokenRefreshToFinish() {
      await new Promise(resolve => {
        const checkInterval = setInterval(() => {
          if (!this.token_refresh_in_progress) {
            clearInterval(checkInterval);
            resolve();
          }
        }, 100);
      });
    },

    tryGetCachedToken(dashboardId, skipCache = false) {
      if (skipCache || !dashboardId) {
        return null;
      }

      const cachedToken = token_cache.get(dashboardId);
      return cachedToken ? { token: cachedToken } : null;
    },

    resolveGuestTokenDashboardId(dashboardId) {
      const targetDashboardId = dashboardId || superset_config.defaultDashboardIds.main;
      if (!targetDashboardId) {
        throw new Error('Dashboard ID is required for guest token fetch.');
      }
      return targetDashboardId;
    },

    async requestGuestToken(targetDashboardId) {
      const response = await fetch(auth_config.guestTokenUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${this.auth_token}`,
          'Accept': 'application/json'
        },
        body: JSON.stringify({ dashboard_id: targetDashboardId, client_app: 'infinity' })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Guest token request failed: ${response.status}, ${errorText}`);
      }

      const rawResponse = await response.text();
      const data = safe_parse_maybe_double_encoded_json(rawResponse);

      if (data.status === 'success' && data.data && data.data.token) {
        token_cache.set(targetDashboardId, data.data.token);
        return data.data;
      }

      throw new Error(data.message || 'Failed to get guest token');
    },

    async fetchGuestToken(dashboardId = null, { skipCache = false } = {}) {

      this.ensureAuthToken();

      if (this.token_refresh_in_progress) {
        await this.waitForTokenRefreshToFinish();
        const waitedCachedToken = this.tryGetCachedToken(dashboardId, skipCache);
        if (waitedCachedToken) {
          return waitedCachedToken;
        }
      }

      const targetDashboardId = this.resolveGuestTokenDashboardId(dashboardId);
      const cachedToken = this.tryGetCachedToken(targetDashboardId, skipCache);
      if (cachedToken) {
        return cachedToken;
      }

      this.token_refresh_in_progress = true;
      try {
        return await this.requestGuestToken(targetDashboardId);
      } catch (error) {
        throw new Error(`Failed to fetch guest token for dashboard ${dashboardId || 'main'}: ${error.message}`);
      } finally {
        this.token_refresh_in_progress = false;
      }
    },

    async fetch_guest_token(options = {}) {
      const currentDashboardId = this.current_dashboard_id;
      if (!is_valid_dashboard_id(currentDashboardId)) {
        throw new Error('No valid dashboard selected for guest token fetch.');
      }
      try {
        const tokenData = await this.fetchGuestToken(currentDashboardId, options);
        if (!tokenData || !tokenData.token) throw new Error('No token received');
        return tokenData.token;
      } catch (error) {
        console.error('Failed to fetch guest token:', error);
        throw error;
      }
    },

    async initializeSupersetSDK() {
      if (typeof window.supersetEmbeddedSdk === 'undefined') await this.loadSupersetSDK();
      this.sdk_loaded = true;
      return true;
    },

    loadSupersetSDK() {
      return new Promise((resolve, reject) => {
        if (typeof window.supersetEmbeddedSdk !== 'undefined') {
          resolve();
          return;
        }
        const script = document.createElement('script');
        script.src = superset_sdk_url;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('Failed to load Superset SDK'));
        document.head.appendChild(script);
      });
    },

    async configureSupersetSDK() {
      if (!window.supersetEmbeddedSdk) throw new Error('Superset SDK not loaded');
      this.sdk_configured = true;
    },

    // ========== DASHBOARD EMBEDDING ==========
    async embedDashboard(containerId, dashboardId, forceRefreshToken = false) {
      try {
        if (!is_valid_dashboard_id(dashboardId)) {
          throw new Error(`Invalid dashboard ID for embed: ${dashboardId}`);
        }

        // Skip if already embedding the same dashboard
        if (this.current_embedding_dashboard_id === dashboardId) {
          return;
        }
        this.current_embedding_dashboard_id = dashboardId;

        this.iframe_loading = true;
        this.iframe_error = false;

        const mountPoint = document.getElementById(containerId);
        if (!mountPoint) throw new Error(`Mount point not found: ${containerId}`);

        mountPoint.innerHTML = '';

        // Always request fresh token when embedding a new dashboard
        token_cache.tokens.delete(dashboardId);

        if (!window.supersetEmbeddedSdk) {
          await this.initializeSupersetSDK();
          await this.configureSupersetSDK();
        }

        // Set up message listener once
        this._setupMessageListener();

        await window.supersetEmbeddedSdk.embedDashboard({
          id: dashboardId,
          supersetDomain: superset_config.baseUrl,
          mountPoint: mountPoint,
          fetchGuestToken: async () => {
            const tokenData = await this.fetchGuestToken(dashboardId, {
              skipCache: false
            });
            return tokenData.token;
          },
          dashboardUiConfig: {
            hideTitle: true,
            hideTab: true,
            filters: { visible: false, expanded: false }
          }
        });

        this.iframe_loading = false;
        this.embedded_dashboards.add(containerId);
      } catch (error) {
        console.error('Failed to embed dashboard:', error);
        this.iframe_loading = false;
        this.iframe_error = true;
      } finally {
        this.current_embedding_dashboard_id = null;
      }
    },

    cleanupDashboards() {
      const containers = ['dashboard-main', 'dashboard-reports', 'dashboard-analytics'];
      containers.forEach(containerId => {
        const container = document.getElementById(containerId);
        if (container) container.innerHTML = '';
      });
      this.embedded_dashboards.clear();
    },

    // ========== NAVIGATION METHODS ==========
    handleBreadcrumbClick(breadcrumbId) {

      if (breadcrumbId === 'main') {
        // Clicked on "Centre Dashboard" - go back to global view
        this.goBack();
      } else {
        // Find the clicked breadcrumb item
        const clickedCrumb = this.breadcrumb.find(crumb => crumb.id === breadcrumbId);
        if (!clickedCrumb) return;

        const index = this.breadcrumb.findIndex(crumb => crumb.id === breadcrumbId);

        // If we clicked on the last item (current view), do nothing
        if (index === this.breadcrumb.length - 1) return;

        // Navigate to the clicked property
        const tile = this.findTile(breadcrumbId);
        if (tile) {
          this.selected_property = tile;
          this.current_dashboard_type = 'property';

          // Default to executive summary when navigating via breadcrumb
          this.active_menu_item = 'portfolio-comparisons';
          this.current_view = breadcrumbId;

          // Trim breadcrumb to only show up to clicked item
          this.breadcrumb = this.breadcrumb.slice(0, index + 1);

          this.$nextTick(() => {
            this.embedDashboard(`dashboard-${breadcrumbId}`, this.current_dashboard_id);
          });
        }
      }
    },

    async goBack() {
      // Clear token cache for property dashboards
      this.cleanupPropertyTokenCache();

      this.current_view = 'main';
      this.breadcrumb = [{ id: 'main', title: 'Centre Dashboard' }];
      this.cleanupDashboards();
      this.current_dashboard_type = 'global';
      this.active_menu_item = 'portfolio-comparisons';
      this.selected_property = null;
      globalState.setActiveProperty(null);

      // Force refresh token for main dashboard
      token_cache.clear(); // Clear all cached tokens

      this.$nextTick(async () => {
        try {
          // Force fresh token fetch for main dashboard
          await this.embedDashboard('dashboard-main', this.current_dashboard_id);
        } catch (error) {
          console.error('Failed to load main dashboard:', error);
        }
      });
    },

    // Helper to clean property-specific tokens
    cleanupPropertyTokenCache() {
      if (this.selected_property) {
        const propertyName = this.selected_property.title;
        const propertyDashboards = this.property_specific_dashboards[String(propertyName)];

        if (propertyDashboards) {
          Object.values(propertyDashboards).forEach(dashboardId => {
            token_cache.tokens.delete(dashboardId);
          });
        }
      }
    },

    async handleGlobalMenuSelected(menuItem) {
      this.active_menu_item = menuItem;
      this.current_dashboard_type = 'global';
      this.current_view = 'main';
      this.selected_property = null;

      // Clear all cached tokens when switching to global view
      token_cache.clear();
      this.current_embedding_dashboard_id = null;

      // Update breadcrumb based on menu selection
      if (menuItem === 'portfolio-comparisons') {
        this.breadcrumb = [{ id: 'main', title: 'Centre Dashboard' }];
      }

      this.$nextTick(async () => {
        try {
          // Always request fresh token when switching dashboards
          await this.embedDashboard('dashboard-main', this.current_dashboard_id);
        } catch (error) {
          console.error('Failed to load global dashboard:', error);
        }
      });
    },

    async handlePropertyMenuSelected(data) {
      this.active_menu_item = data.menuItem;
      this.current_dashboard_type = 'property';

      const tile = this.findTile(data.propertyId);
      if (!tile) return;

      this.selected_property = tile;
      globalState.setActiveProperty(tile);
      this.current_view = data.propertyId;

      this.breadcrumb = [
        { id: 'main', title: 'Centre Dashboard' },
        { id: data.propertyId, title: `${data.propertyName}` }
      ];

      // Clear cached tokens when switching properties
      token_cache.tokens.delete(this.current_dashboard_id);
      this.current_embedding_dashboard_id = null;

      await this.$nextTick();
      const dashboardId = this.current_dashboard_id;
      if (!is_valid_dashboard_id(dashboardId)) {
        console.warn(`No valid dashboard available for menu item ${data.menuItem} on property ${data.propertyName}`);
        this.iframe_error = true;
        this.iframe_loading = false;
        return;
      }

      try {
        await this.embedDashboard(`dashboard-${data.propertyId}`, dashboardId);
      } catch (error) {
        console.error('Failed to load property dashboard:', error);
      }
    },



    async handleTileClick(tileId) {
      const tile = this.findTile(tileId);
      if (!tile) return;

      this.selected_property = tile;
      globalState.setActiveProperty(tile);
      this.current_dashboard_type = 'property';
      this.active_menu_item = 'operations-overview';

      // Set breadcrumb with proper structure
      this.breadcrumb = [
        { id: 'main', title: 'Centre Dashboard' },
        { id: tileId, title: tile.title }
      ];

      this.current_view = tileId;
      this.iframe_loading = true;
      this.iframe_error = false;

      const containerId = `dashboard-${tileId}`;
      const dashboardId = this.current_dashboard_id;

      if (!is_valid_dashboard_id(dashboardId)) {
        console.warn(`No valid dashboard available for property tile ${tileId}`);
        this.iframe_error = true;
        this.iframe_loading = false;
        return;
      }

      try {
        await this.$nextTick();
        await this.embedDashboard(containerId, dashboardId);
      } catch (error) {
        console.error('Failed to load property dashboard:', error);
        this.iframe_error = true;
        this.iframe_loading = false;
      }
    },

    // ========== HELPER METHODS ==========
    findTile(tileId) {
      const search = (arr) => {
        for (let item of arr) {
          if (item.id === tileId) return item;
          if (item.children) {
            const found = search(item.children);
            if (found) return found;
          }
          if (item.tileCards && Array.isArray(item.tileCards)) {
            const foundInTileCards = search(item.tileCards);
            if (foundInTileCards) return foundInTileCards;
          }
        }
        return null;
      };
      return search(this.tiles);
    },

    getTileCards() {
      // If we're in a property view and have selected property
      if (this.is_property_view && this.selected_property) {
        return this.selected_property.tileCards || [];
      }
      return [];
    },

    hasTileCards() {
      return this.is_property_view && this.selected_property &&
        this.selected_property.tileCards &&
        this.selected_property.tileCards.length > 0;
    },

    getCurrentTitle() {
      const tile = this.findTile(this.current_view);
      return tile ? tile.title : 'Overview';
    },

    isDetailView(tileId) {
      return tileId.includes('-') && tileId !== 'main';
    },

    // ========== UI HELPERS ==========
    handleResize() {
      this.window_width = window.innerWidth;
      this.is_mobile = window.innerWidth < 768;
      if (this.is_mobile) {
        this.right_panel_collapsed = false;
      } else {
        this.mobile_sidebar_open = false;
      }
    },

    getMainContentClass() {
      if (this.is_mobile) return 'col-12';
      return this.right_panel_collapsed ? 'col-12' : 'col-lg-12 col-md-12 m-0 p-0';
    },

    getChildrenContentClass() {
      return ''; // no bootstrap column classes
    },

    getChildrenSidebarClass() {
      return ''; // no bootstrap column classes
    },


    toggleRightPanel() {
      if (this.is_mobile) return;
      this.right_panel_collapsed = !this.right_panel_collapsed;
    },

    toggle_mobile_sidebar() {
      this.mobile_sidebar_open = !this.mobile_sidebar_open;
    },

    close_sidebar() {
      this.mobile_sidebar_open = false;
    },

    getPropertyMunicipality() {
      if (this.selected_property) {
        const title = this.selected_property.title || '';

        if (title.includes('Ganyesa'))
          return 'Kagisano/Molopo Local Municipality - Ganyesa';

        if (title.includes('Lesedi'))
          return 'City of Ekurhuleni (CoE) - Vosloorus';

        if (title.includes('Ekuphumleni'))
          return 'Ndlambe Local Municipality - Kenton-On-Sea';

        if (title.includes('Thembokwezi'))
          return 'City of Cape Town - Cape Town';

        // ---- placeholders (data later) ----
        if (title.includes('Erica')) return 'Municipality - TBD';
        if (title.includes('Amsterdam')) return 'Municipality - TBD';
        if (title.includes('Madadeni')) return 'Municipality - TBD';
        if (title.includes('Aliwal North')) return 'Municipality - TBD';
        if (title.includes('Rustenburg')) return 'Municipality - TBD';
        if (title.includes('Ngqamakhwe')) return 'Municipality - TBD';
        if (title.includes('Mkhondo')) return 'Municipality - TBD';
      }

      return 'Municipality';
    },

    getPropertyWaterUtility() {
      if (this.selected_property) {
        const title = this.selected_property.title || '';

        if (title.includes('Lesedi'))
          return 'Ekurhuleni Water';

        if (title.includes('Thembokwezi'))
          return 'City of Cape Town Water';

        if (title.includes('Ekuphumleni'))
          return 'Ndlambe Local Municipality Water';

        if (title.includes('Ganyesa'))
          return 'Kagisano/Molopo Local Municipality Water';

        // ---- placeholders ----
        if (title.includes('Erica')) return 'Water Utility - TBD';
        if (title.includes('Amsterdam')) return 'Water Utility - TBD';
        if (title.includes('Madadeni')) return 'Water Utility - TBD';
        if (title.includes('Aliwal North')) return 'Water Utility - TBD';
        if (title.includes('Rustenburg')) return 'Water Utility - TBD';
        if (title.includes('Ngqamakhwe')) return 'Water Utility - TBD';
        if (title.includes('Mkhondo')) return 'Water Utility - TBD';
      }

      return 'Water Utility';
    },

    getPropertyTariffRegime() {
      if (this.selected_property) {
        const title = this.selected_property.title || '';

        if (
          title.includes('Lesedi') ||
          title.includes('Ekuphumleni') ||
          title.includes('Thembokwezi')
        ) {
          return 'TIME OF USE';
        }

        if (title.includes('Ganyesa'))
          return 'Tariff Regime - TBD';

        // ---- placeholders ----
        if (title.includes('Erica')) return 'Tariff Regime - TBD';
        if (title.includes('Amsterdam')) return 'Tariff Regime - TBD';
        if (title.includes('Madadeni')) return 'Tariff Regime - TBD';
        if (title.includes('Aliwal North')) return 'Tariff Regime - TBD';
        if (title.includes('Rustenburg')) return 'Tariff Regime - TBD';
        if (title.includes('Ngqamakhwe')) return 'Tariff Regime - TBD';
        if (title.includes('Mkhondo')) return 'Tariff Regime - TBD';
      }

      return 'Tariff Information';
    }
  }
};